
import { Component, OnDestroy, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';

import moment from 'moment-timezone';
import { Subscription, combineLatest, lastValueFrom, of } from 'rxjs';
import { map, filter, concatMap, catchError } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { AccountManagementService } from '../../services/account-management.service';
import { MediaSubscriptionPreferenceService } from '../../services/media-subscription-preference.service';
import { UserModel } from '../../../../Core/Users/models/user.model';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { AccountModel } from '../../../../Core/Users/models/account.model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { UserProfileDialogComponent } from '../UserProfile/UserProfileDialog/user-profile-dialog.component';
import { UsersApi } from '../../../../api/users/users.api';
import { RightSidebarComponent } from '../../../../Common/Layout/components/RightSidebar/right-sidebar.component';
import { LicensesEnum } from '../../../../api/monitoring/licenses.enum';
import { SubscriptionLicenses } from '../../models/monitoring.configs.model';
import { TimeModel } from '../../../../Common/Forms/models/time.model';
import { meridiansEnum } from '../../../../Common/Forms/enums/meridians.enum';
import { MinuteModel } from '../../../../Common/Forms/models/minute.model';
import { TwelveHourModel } from '../../../../Common/Forms/models/twelve-hour.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { WebsocketAwareNeptuneService } from '../../../../Bootstrap/services/websocket-aware-neptune.service';
import { HttpVerbsEnum } from '../../../../api/shared/enums/http-verbs.enum';
import { LoginApi } from '../../../../api/login/login.api';
import { MfaTypesEnum } from '../../../../Core/Users/models/mfa.model';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { SsoProviderIdEnum, SsoProvider } from '../../../../Core/Users/models/sso-provider.model';
import { ConfirmDialogModel } from '../../../../Common/Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { copy } from '../../../../Common/Misc/helpers/copy/copy.helper';
import { TabsComponent } from '../../../../Common/Layout/components/Tabs/tabs.component';
import { PreferencesRegionSelectDialogService } from '../../services/preferences-region-select-dialog.service';
import { JournodexGeographyModel } from '../../../../Common/Journodex/models/journodex-geography.model';
import { IncludeOrExclude } from '../../../../Common/Dialogs/services/data-select-dialog.service.interface';
import { DataSelectDialogModel } from '../../../../Common/Dialogs/models/data-select-dialog.model';
import { BasicFilterApiModel } from '../../../../api/shared/models/basic-filter.api.model';
import { DataSelectDialogComponent } from '../../../../Common/Dialogs/components/DataSelectDialog/data-select-dialog.component';
import { TargetingAccountPreferencesApiModel } from '../../../../api/targeting/model/targeting-account-preferences.api.model';
import { JournodexRegionsService } from '../../../../Common/Journodex/services/journodex-regions.service';
import { AccountStatusApi } from '../../../../api/contentLibrary/models/account-status.api.model';
import { AccountNoteModel } from '../../models/account-note.model';
import { AccountNoteChange, AccountNotesComponent } from '../AccountNotes/account-notes.component';
import { EventsApi } from '../../../../api/events/events-api';
import { EventResourceTypeEnum } from '../../../../api/events/enum/event-resource-type.enum';
import { MonitoringQuotaTypesEnum } from '@api/mentions/enum/monitoring-quota-types.enum';
import { AgentConfigurationApi } from '../../../../api/agents/models/agent-configuration.api.model';

interface SsoProviderRadioOption {
    label: string;
    provider: SsoProvider;
}

enum AdminAccountPreferencesTabsEnum {
    general,
    discover,
    monitor,
    socialListening
}

@Component({
    selector: 'agpr-admin-account-preferences',
    templateUrl: './admin-account-preferences.component.html',
    styleUrls: ['./admin-account-preferences.component.scss'],
    standalone: false
})
export class AdminAccountPreferencesComponent implements OnDestroy, OnInit, AfterViewInit {

    @ViewChild('rightSidebar') public rightSidebar: RightSidebarComponent;
    @ViewChild('tabs') public tabs: TabsComponent;
    @ViewChild('accountNotes') accountNotes: AccountNotesComponent;

    selectedTabIndex: AdminAccountPreferencesTabsEnum = AdminAccountPreferencesTabsEnum.general;

    canGhostIn: boolean = false;
    currentUser: UserModel;

    subscriptions: Subscription[] = [];

    userSearchResults: UserModel[];
    accountSearchResults: AccountModel[];

    selectedUser: UserModel;
    selectedAccountUsers: UserModel[] = [];
    filteredUsers: UserModel[] = [];
    selectedUserOption: DropdownOptionModel;

    selectedAccountNotes: AccountNoteModel[] = [];

    selectedAccount: AccountModel = new AccountModel();
    userAccountOptions: DropdownOptionModel[] = [];
    selectedAccountOption: DropdownOptionModel;

    hasMonitoring: boolean = false;
    hasTargeting: boolean = false;
    hasNewswire: boolean = false;
    hasClassicTargeting: boolean = false;
    hasSocial: boolean = false;
    disablePreserveData: boolean = true;
    hasMediaDatabaseApis: boolean = false;
    hasMediaMonitoringApis: boolean = false;

    isReadOnly: boolean = false;

    loading: boolean = false;
    userToAdd: UserModel;
    autocompleteSelectedUser;

    userFilterString: string;

    prominenceInUse: number;

    dataPreserveExpireDate: string = 'N/A';
    days90: number = 7776000000;

    partnerResellerDropdownOptions: DropdownOptionModel[] = [];
    contentLibraryStorageSettings: AccountStatusApi;

    selectedPartnerReseller: DropdownOptionModel;
    isPartnerResellerAccount: boolean = false;

    mediaDatabaseApisDropdownOptions: DropdownOptionModel[] = [];
    selectedMediaDatabaseApiReseller: DropdownOptionModel;

    licenses: SubscriptionLicenses;
    socialLicenses: SubscriptionLicenses;

    agentConfiguration: AgentConfigurationApi | null = null;

    ssoEnabled: boolean = false;
    selectedSsoProviderId: number = null;
    ssoProviderRadioOptions: SsoProviderRadioOption[] = [];

    agilityPlatform: boolean = false;
    accessToken: string;
    clientId: string;

    startTime: TimeModel;
    expireTime: TimeModel;
    lastAutoRenewDate: string = null;

    targetingPreferences: TargetingAccountPreferencesApiModel;
    hasNetworksError: boolean;
    saveErrorTooltip: string;
    hasInvalidContractTimeRange: boolean = false;
    hasContractTimeBoundaryWarning: boolean = false;

    readonly id = 'accountDetails';
    readonly ssoProviderEnum = SsoProviderIdEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly servicesEnum = userPermissionsEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly mfaTypesEnum = MfaTypesEnum;
    readonly allAccountPermissions = [
        {
            enum: userPermissionsEnum.advancedTargeting,
            displayString: 'AMT.permission.mediaRelations'
        },
        {
            enum: userPermissionsEnum.advancedMonitoring,
            displayString: 'AMT.permission.mediaIntelligence'
        },
        {
            enum: userPermissionsEnum.newswireplus,
            displayString: 'AMT.permission.newswirePlus'
        },
        {
            enum: userPermissionsEnum.newsroom,
            displayString: 'AMT.permission.newsroom'
        },
        {
            enum: userPermissionsEnum.socialIntelligence,
            displayString: 'AMT.permission.socialListening'
        },
        {
            enum: userPermissionsEnum.openAI,
            displayString: 'AMT.permission.openAI',
            children: [
                {
                    enum: userPermissionsEnum.precisionClipper,
                    displayString: 'AMT.permission.precisionClipping',
                    disabled: (permissions: userPermissionsEnum[]): boolean => {
                        return !permissions.includes(userPermissionsEnum.openAI);
                    }
                },
                {
                    enum: userPermissionsEnum.agenticFeature,
                    displayString: 'AMT.permission.agenticFeature',
                    disabled: (permissions: userPermissionsEnum[]): boolean => {
                        return !permissions.includes(userPermissionsEnum.openAI);
                    }
                },
                {
                    enum: userPermissionsEnum.visibilityIntelligence,
                    displayString: 'AMT.permission.visibilityIntelligence',
                    disabled: (permissions: userPermissionsEnum[]): boolean => {
                        return !permissions.includes(userPermissionsEnum.openAI);
                    }
                },
            ]
        }
    ];
    readonly maxMonitorQuestionLimit: number = 1000;

    private readonly globalSsoProviderRadioOptions: SsoProviderRadioOption[] = [
        {
            label: 'Sso.amt.google',
            provider: new SsoProvider({ id: SsoProviderIdEnum.google, name: 'Google' })
        },
        {
            label: 'Sso.amt.microsoft',
            provider: new SsoProvider({ id: SsoProviderIdEnum.microsoft, name: 'Microsoft' })
        },
    ];


    constructor(
        public accountManagementService: AccountManagementService,
        public usersApi: UsersApi,
        public coreDialogService: CoreDialogService,
        private router: Router,
        private mediaSubscriptionPreferenceService: MediaSubscriptionPreferenceService,
        private currentUserStateService: CurrentUserStateService,
        private websocketAwareNeptuneService: WebsocketAwareNeptuneService,
        private loginApi: LoginApi,
        private localizationService: LocalizationService,
        private messageService: MessageService,
        public regionSelectDialogService: PreferencesRegionSelectDialogService,
        public journodexRegionsService: JournodexRegionsService,
        private eventsApi: EventsApi,
    ) {
    }

    ngOnInit(): void {
        this.subscriptions = [];
        this.subscriptions.push(
            this.accountManagementService.getSelectedUser().subscribe(result => {
                this.selectedUser = result;
                if (result) {
                    this.selectedUserOption = new DropdownOptionModel(result.username, result);
                }
            }),
            combineLatest([
                this.accountManagementService.getSelectedAccount().pipe(filter(x => !!x)),
                this.usersApi.getAllMediaApiPartnerResellers().pipe(catchError(x => of([]))),
                this.accountManagementService.getAgentConfiguration()
            ]).subscribe(([result, mediaApiResellerOptions, agentConfiguration]) => {
                this.selectedAccount = result;
                this.loadLastRenewalDate(result.id);
                this.refreshMediaDatabaseApiFields();
                this.selectedAccountOption = new DropdownOptionModel(result.name, result);
                this.hasMonitoring = this.accountHasPermission(userPermissionsEnum.advancedMonitoring);
                this.hasTargeting = this.accountHasPermission(userPermissionsEnum.advancedTargeting);
                this.hasNewswire = this.accountHasPermission(userPermissionsEnum.newswireplus);
                this.hasClassicTargeting = this.accountHasPermission(userPermissionsEnum.targeting);
                this.hasSocial = this.accountHasPermission(userPermissionsEnum.socialIntelligence);
                this.hasMediaDatabaseApis = this.accountHasPermission(userPermissionsEnum.mediaDatabaseApi);
                this.hasMediaMonitoringApis = this.accountHasPermission(userPermissionsEnum.mediaMonitoringApi);

                this.agentConfiguration = agentConfiguration ?? { accountId: this.selectedAccount.id, monitorQuestionLimit: 0 };

                this.setSubscriptionQuotaInfo();

                this.selectedSsoProviderId = this.selectedAccount.ssoProvider?.id ? this.selectedAccount.ssoProvider.id : null;
                this.ssoEnabled = !!this.selectedSsoProviderId;
                this.ssoProviderRadioOptions = this.globalSsoProviderRadioOptions.slice();
                if (this.selectedAccount.availableSsoProviders?.length) {
                    this.ssoProviderRadioOptions.push(...this.selectedAccount.availableSsoProviders.map(provider => {
                        return {
                            label: provider.name,
                            provider: provider
                        };
                    }));
                }

                this.isPartnerResellerAccount = !!this.selectedAccount.partnerResellerId;
                this.loadPartnerResellerOptions().then(() => {
                    if (this.selectedAccount) {
                        this.selectedPartnerReseller = this.partnerResellerDropdownOptions.find( x => x.value === this.selectedAccount.partnerResellerId);
                    }
                });

                this.mediaDatabaseApisDropdownOptions = [];
                mediaApiResellerOptions.forEach(partnerReseller => {
                    this.mediaDatabaseApisDropdownOptions.push({label: partnerReseller.name, value: partnerReseller.id});
                });

                this.selectedMediaDatabaseApiReseller
                    = this.mediaDatabaseApisDropdownOptions.find( x => x.value === this.selectedAccount.mediaApiPartnerResellerId);

                if (!this.selectedMediaDatabaseApiReseller) {
                    this.selectedMediaDatabaseApiReseller = this.mediaDatabaseApisDropdownOptions[0];
                }
                // Always default back to general when changing accounts
                this.selectedTabIndex = AdminAccountPreferencesTabsEnum.general;
            }),
            this.accountManagementService.getSelectedAccountUsers().subscribe(result => {
                this.selectedAccountUsers = result;
                this.filterUsers();
            }),
            this.accountManagementService.getSelectedUserAccounts().subscribe(result => {
                if (result?.length > 0) {
                    result.forEach(account => this.userAccountOptions.push({label: account.name, value: account}));
                }
            }),
            this.accountManagementService.getReadOnly().subscribe(result => {
                this.isReadOnly = result;
            }),
            this.accountManagementService.loading$.subscribe(x => {
                this.loading = x;
                if (!this.loading) {
                    this.setPreserveDataDisable();
                }
            }),

            this.accountManagementService.contentLibraryStorageSettings$.pipe(filter(x => !!x)).subscribe(status => {
                this.contentLibraryStorageSettings = status;
            }),
            this.accountManagementService.userToAdd$.subscribe(user => {
                this.userToAdd = user;
                if (!this.userToAdd) {
                    this.autocompleteSelectedUser = null;
                }
            }),
            this.accountManagementService.prominenceInUse$.subscribe(x => this.prominenceInUse = x),
            this.mediaSubscriptionPreferenceService.licenses$.subscribe(licenses => this.licenses = licenses),
            this.accountManagementService.getSocialLicenses().subscribe(socialLicenses => this.socialLicenses = socialLicenses),
            this.currentUserStateService.getUser().subscribe(user => {
                this.currentUser = user;
                this.canGhostIn = user.permissions.includes(userPermissionsEnum.accountGhostIn) && user.neptuneOnly;
            }),
            combineLatest([
                this.accountManagementService.getSelectedAccount().pipe(filter(x => !!x)),
                this.currentUserStateService.getPreferences().pipe(filter(x => !!x))
            ]).subscribe(([account, preferences]) => {
                this.getContractTime(account, preferences);
            }),
            this.accountManagementService.getTargetingAccountPreferences().subscribe(prefs => {
                if (prefs) {
                    this.targetingPreferences = prefs;
                }
            }),
            this.accountManagementService.selectedAccountNotes$.subscribe(notes => this.selectedAccountNotes = notes),
        );
    }

    ngAfterViewInit(): void {
        if (this.selectedAccount?.active) {
            setTimeout(() => {
                this.rightSidebar.unselectTab();
                this.rightSidebar.selectTabByIndex(0);
            },
            0);
        }
    }

    public changeTabs(index: number): void {
        this.selectedTabIndex = index;
    }

    setPreserveDataDisable() {
        // if expired date is greater than 90 days
        if (new Date().valueOf() - this.selectedAccount.expiryDate?.toDate()?.valueOf() >= this.days90) {
            if (this.selectedAccount.preserveData) {
                this.disablePreserveData = false;
            } else {
                this.disablePreserveData = true;
            }
        } else {
            this.disablePreserveData = false;
        }

        this.setPreserveExpireDate();
    }

    showPreserveDataWarning() {
        if (!this.selectedAccount.preserveData) {
            const confirmConfig: ConfirmDialogModel = {
                inputs: {
                    id: 'AMT_accountSettings_preserveDataDialog',
                    heading: 'AMT.accountSettings.preserveData',
                    alertMessage: 'AMT.accountSettings.preserveData.warning',
                    message: 'AMT.accountSettings.preserveData.areYouSure',
                    alertType: messageSeverityEnum.warning
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (!confirmed) {
                            this.selectedAccount.preserveData = true;
                        }
                        this.setPreserveExpireDate();
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
        } else {
            this.setPreserveExpireDate();
        }
    }

    setPreserveExpireDate() {
        // if expired date has passed
        if (this.selectedAccount.preserveData || (this.selectedAccount.expiryDate?.toDate()?.valueOf() - new Date().valueOf() > 0)) {
            this.dataPreserveExpireDate = 'N/A';
        } else {
            this.dataPreserveExpireDate = moment(this.selectedAccount.expiryDate?.toDate()?.valueOf() + this.days90).format('MM/DD/YYYY HH:MM');
        }
    }

    loadLastRenewalDate(accountId: number): void {
        this.lastAutoRenewDate = null;
        this.eventsApi.searchAny({ accountId, resourceType: EventResourceTypeEnum.ACCOUNT_RENEWAL }).subscribe({
            next: events => {
                if (events?.length) {
                    const latest = events.sort((a, b) => new Date(b.date + 'Z').getTime() - new Date(a.date + 'Z').getTime())[0];
                    this.lastAutoRenewDate = moment(latest.date + 'Z').format('YYYY-MM-DD');
                }
            }
        });
    }

    loadPartnerResellerOptions(): Promise<void> {
        return lastValueFrom(
            this.usersApi.getAllPartnerResellers().pipe(
                map(partnerResellers => {
                    this.partnerResellerDropdownOptions = [];
                    if (partnerResellers) {
                        partnerResellers.forEach(partnerReseller => {
                            if (partnerReseller.id === 3) {
                                // Typo in DB can't be fixed, overriding Burrelles label here
                                this.partnerResellerDropdownOptions.push({label: 'Burrelles', value: partnerReseller.id});
                            } else {
                                this.partnerResellerDropdownOptions.push({label: partnerReseller.name, value: partnerReseller.id});
                            }
                        });
                    }
                })
            )
        );
    }

    autocompleteUser(searchString: string) {
        this.accountManagementService.searchUsers(searchString).subscribe(result => {
            this.userSearchResults = result;
        });
    }

    clearAccount() {
        this.selectedAccount = null;
        this.refreshMediaDatabaseApiFields();
        this.selectedAccountOption = null;
        this.userAccountOptions = [];
        this.router.navigate([]);
    }

    ngOnDestroy(): void {
        for (const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }
    }

    gotoAccount(accountId: number) {
        this.router.navigate(
            [],
            {
                queryParams: {
                    accountId: accountId
                }
            }
        );
    }

    openUserEditorDialog(userId?: number, addToAccountId?: number, passedUser?: UserModel) {
        if (!userId) {
            const user =  new UserModel();
            user.accountsIds = [this.selectedAccount.id];
            this.coreDialogService.open(UserProfileDialogComponent, {
                inputs: {
                    user: user,
                    addToCurrentAccount: true
                },
                outputs: {
                    active: (state: boolean) => {
                        user.isActive = state;
                    }
                }
            });
        } else {
            this.usersApi.getUser(userId).subscribe(user => {
                this.coreDialogService.open(UserProfileDialogComponent, {
                    inputs: {
                        user: new UserModel(user)
                    },
                    outputs: {
                        active: (state: boolean) => {
                            passedUser.isActive = state;
                        }
                    }
                });
            });
        }
    }

    getContractTime(selectedAccount: AccountModel, preferences: PreferencesModel): void {
        const startDate = moment(selectedAccount.startDate).utc();
        this.selectedAccount.startDate = startDate;

        const expiryDate = moment(selectedAccount.expiryDate).utc();
        this.selectedAccount.expiryDate = expiryDate;

        const startHour = startDate.hour();
        const startMinute = startDate.minute();
        this.startTime = new TimeModel(preferences.timeFormat, startHour as TwelveHourModel,
            startMinute as MinuteModel,  ((startHour >= 12) ? meridiansEnum.PM : meridiansEnum.AM), null, 0);

        const expireHour = expiryDate.hour();
        const expireMinute = expiryDate.minute();
        this.expireTime = new TimeModel(preferences.timeFormat, expireHour as TwelveHourModel,
            expireMinute as MinuteModel,  ((expireHour >= 12) ? meridiansEnum.PM : meridiansEnum.AM), null, 0);

        this.validateContractDateTimeRange();
    }

    isContractSameDay(): boolean {
        return !!this.selectedAccount?.startDate?.isSame(this.selectedAccount?.expiryDate, 'day');
    }

    validateContractDateTimeRange(): void {
        this.hasContractTimeBoundaryWarning = false;
        this.hasInvalidContractTimeRange = !!this.selectedAccount?.startDate?.isSameOrAfter(this.selectedAccount?.expiryDate);
    }

    onContractTimeBoundaryReached(): void {
        this.hasContractTimeBoundaryWarning = true;
    }

    /**
     *  Handle hour and minute when start or end time changes
     *  If the hour is in PM, noon needs to map to 12, all other hours get 12 added to them to become 24 hour time
     *  If the hour is in the AM, midnight needs to map to 0, all other hours remain unchanged
     */
    startOrEndTimeChanged(): void {
        if (this.startTime.meridian === meridiansEnum.PM) {
            if (this.startTime.hour === 12) {
                this.selectedAccount.startDate.set({hour: 12});
            } else {
                this.selectedAccount.startDate.set({hour: this.startTime.hour + 12});
            }
        } else {
            if (this.startTime.hour === 12) {
                this.selectedAccount.startDate.set({hour: 0});
            } else {
                this.selectedAccount.startDate.set({hour: this.startTime.hour});
            }
        }

        if (this.expireTime.meridian === meridiansEnum.PM) {
            if (this.expireTime.hour === 12) {
                this.selectedAccount.expiryDate.set({hour: 12});
            } else {
                this.selectedAccount.expiryDate.set({hour: this.expireTime.hour + 12});
            }
        } else {
            if (this.startTime.hour === 12) {
                this.selectedAccount.expiryDate.set({hour: 0});
            } else {
                this.selectedAccount.expiryDate.set({hour: this.expireTime.hour});
            }
        }

        this.selectedAccount.startDate.set({minute: this.startTime.minute});
        this.selectedAccount.expiryDate.set({minute: this.expireTime.minute});

        this.validateContractDateTimeRange();
    }

    onChangeStartDate(event) {
        this.selectedAccount.startDate = moment(event).utc();
        this.startOrEndTimeChanged();
    }

    onChangeExpireDate(event) {
        this.selectedAccount.expiryDate = moment(event).utc();
        this.startOrEndTimeChanged();
    }

    openCreateContactDialog() {
        this.openUserEditorDialog(null, this.selectedAccount.id);
    }

    editUser(user: UserModel) {
        this.openUserEditorDialog(user.id, undefined, user);
    }

    removeUserFromAccount(user: UserModel) {
        this.accountManagementService.removeUserFromAccount(user, this.selectedAccount);
    }

    togglePreferenceVisibilities(selected?: boolean, permission?: userPermissionsEnum) {
        if (!this.selectedAccount) {
            return;
        }

        switch (permission) {
            case userPermissionsEnum.advancedMonitoring:
                this.hasMonitoring = selected;
                break;
            case userPermissionsEnum.advancedTargeting:
                this.hasTargeting = selected;
                break;
            case userPermissionsEnum.newswireplus:
                this.hasNewswire = selected;
                break;
            case userPermissionsEnum.socialIntelligence:
                this.hasSocial = selected;
                this.setSocialLicenses(selected);
                break;
            case userPermissionsEnum.openAI:
                const childPermissions = [userPermissionsEnum.visibilityIntelligence, userPermissionsEnum.precisionClipper];
                if (selected) {
                    childPermissions.forEach(perm => {
                        if (!this.accountHasPermission(perm)) {
                            this.selectedAccount.permissions.push(perm);
                        }
                    });
                } else {
                    this.removePermission(userPermissionsEnum.visibilityIntelligence);
                    this.removePermission(userPermissionsEnum.precisionClipper);
                    this.removePermission(userPermissionsEnum.agenticFeature);
                }

                this.accountManagementService.setOpenAiPermission();
                // Turning AI off takes the agentic permission with it, so the quota panel must follow
                this.accountManagementService.setAgenticFeaturePermission();
                break;
            case userPermissionsEnum.visibilityIntelligence:
                const parentPermission = userPermissionsEnum.openAI;
                if (selected && !this.accountHasPermission(parentPermission)) {
                    this.selectedAccount.permissions.push(parentPermission);
                    this.accountManagementService.setOpenAiPermission();
                }
                if (selected && !this.agentConfiguration) {
                    this.accountManagementService.setMonitorQuestionLimit(0);
                }
                break;
            case userPermissionsEnum.agenticFeature:
                if (selected && !this.accountHasPermission(userPermissionsEnum.openAI)) {
                    this.selectedAccount.permissions.push(userPermissionsEnum.openAI);
                    this.accountManagementService.setOpenAiPermission();
                }

                this.accountManagementService.setAgenticFeaturePermission();
                break;
            case userPermissionsEnum.precisionClipper:
                if (selected && !this.accountHasPermission(userPermissionsEnum.openAI)) {
                    this.selectedAccount.permissions.push(userPermissionsEnum.openAI);
                    this.accountManagementService.setOpenAiPermission();
                }
                break;
        }

        this.setSubscriptionQuotaInfo();
    }

    onAccountChanged(account: AccountModel): void {
        this.selectedAccount = account;
        this.refreshMediaDatabaseApiFields();
        this.gotoAccount(this.selectedAccount.id);
    }

    filterUsers() {
        if (!this.userFilterString) {
            this.filteredUsers = this.selectedAccountUsers;
        } else {
            this.filteredUsers = this.selectedAccountUsers.filter(user => {
                return (!!user.firstName && user.firstName.toLocaleLowerCase().includes(this.userFilterString.toLocaleLowerCase())) ||
                    (!!user.lastName && user.lastName.toLocaleLowerCase().includes(this.userFilterString.toLocaleLowerCase())) ||
                    (!!user.username && user.username.toLocaleLowerCase().includes(this.userFilterString.toLocaleLowerCase())) ||
                    (!!user.email && user.email.toLocaleLowerCase().includes(this.userFilterString.toLocaleLowerCase()));
            });
        }
    }

    toggleAccountActivity() {
        this.loading = true;
        if (this.selectedAccount.active) {
            this.usersApi.deactivateAccount(this.selectedAccount.id).subscribe(done => {
                this.loading = false;
                this.selectedAccount.active = false;
            });
        } else {
            this.usersApi.activateAccount(this.selectedAccount.id).subscribe(done => {
                this.loading = false;
                this.selectedAccount.active = true;
                setTimeout(() => {
                    this.rightSidebar.selectTabByIndex(0);
                },
                0);
            });
        }
    }

    partnerResellerChanged(option: DropdownOptionModel) {
        if (option.value) {
            this.selectedAccount.partnerResellerId = option.value;
        } else {
            this.selectedAccount.partnerResellerId = null;
        }
    }
    isPartnerResellerAccountChanged(isPartnerReseller: boolean) {
        if (!isPartnerReseller) {
            this.selectedAccount.partnerResellerId = null;
        }
    }

    mediaDbApiPartnerResellerChanged(option: DropdownOptionModel) {
        this.selectedAccount.mediaApiPartnerResellerId = option.value;
    }

    scrollToWireQuota(quotaCard: ElementRef): void {
        this.tabs.scrollPanel.scrollIntoView(quotaCard);
    }

    private accountHasPermission(permission: userPermissionsEnum): boolean {
        return !!this.selectedAccount.permissions.find(x => x === permission);
    }

    private addPermission(permission: userPermissionsEnum) {
        if (!this.accountHasPermission(permission)) {
            this.selectedAccount.permissions.push(permission);
        }
    }

    private removePermission(permission: userPermissionsEnum) {
        const index = this.selectedAccount.permissions.indexOf(permission);
        if (index !== -1) {
            this.selectedAccount.permissions.splice(index, 1);
        }
    }

    private setSubscriptionQuotaInfo() {
        const hasSubscriptions = this.accountHasPermission(userPermissionsEnum.mediaSubscriptions);
        this.accountManagementService.toggleMonitoringQuotaGroup(hasSubscriptions, MonitoringQuotaTypesEnum.MEDIA_SUBSCRIPTION);
        this.accountManagementService.toggleMonitoringQuotaGroup(this.hasSocial, MonitoringQuotaTypesEnum.SOCIAL_LISTENING);
    }

    private setSocialLicenses(enable: boolean) {
        this.socialLicenses[LicensesEnum.FACEBOOK] = enable;
        this.socialLicenses[LicensesEnum.INSTAGRAM] = enable;
        this.socialLicenses[LicensesEnum.REDDIT] = enable;
        this.socialLicenses[LicensesEnum.TWITTER] = enable;
        this.socialLicenses[LicensesEnum.YOUTUBE] = enable;
        this.socialLicenses[LicensesEnum.TIKTOK] = enable;
        this.socialLicenses[LicensesEnum.LINKEDIN] = enable;
        this.socialLicenses[LicensesEnum.BLUESKY] = enable;
    }

    ghostIn(user: UserModel) {
        this.loading = true;
        this.currentUserStateService.startGhostIn();
        this.loginApi.ghostIn({ userId: user.id }).pipe(
            map(done => {
                this.currentUserStateService.clearState();
                this.currentUserStateService.loadUserState();
                this.currentUserStateService.ignoreWebsockets = true;
                this.websocketAwareNeptuneService.sendWebsocketEventInstant('/login/ghostin', HttpVerbsEnum.POST);
            }),
            concatMap(done => {
                if (user.primaryAccountId !== this.selectedAccount.id) {
                    return this.currentUserStateService.switchAccount(this.selectedAccount.id);
                }
                return of(null);
            }),
            map(done => {
                this.websocketAwareNeptuneService.disconnectFromWebsocket();
                this.websocketAwareNeptuneService.connectToWebsocket();
            })
        ).subscribe({
            next: () => {
                if (this.currentUserStateService.accountHasJournodex(this.selectedAccount)) {
                    this.currentUserStateService.navigateToJournodex();
                    return;
                }
                this.router.navigate(['Dashboard']);
                // HACK: refresh the page after redirect, we do this because things don't handle refreshing data properly on loadUserState,
                // and localizations don't update either
                requestAnimationFrame(() => {
                    window.location.reload();
                });
            },
            error: () => {
                this.loading = false;
            }
        });
    }

    toggleMfa() {
        if (this.selectedAccount.isMfaEnabled) {
            this.selectedAccount.isMfaRequired = false;
            this.selectedAccount.enabledMfaTypes = Object.values(this.mfaTypesEnum);
            this.ssoEnabled = false;
            this.toggleSSO();
        } else {
            this.selectedAccount.isMfaRequired = null;
            this.selectedAccount.enabledMfaTypes = null;
        }
    }

    toggleSSO() {
        if (this.ssoEnabled) {
            this.selectedAccount.ssoProvider = this.ssoProviderRadioOptions[0].provider;
            this.selectedSsoProviderId = this.selectedAccount.ssoProvider.id;
            this.selectedAccount.isMfaEnabled = false;
            this.toggleMfa();
        } else {
            this.selectedAccount.ssoProvider = null;
            this.selectedSsoProviderId = null;
        }
    }

    updateSSO(ssoProviderId: number) {
        this.selectedAccount.ssoProvider = this.ssoProviderRadioOptions.find(option => option.provider.id === ssoProviderId)?.provider;
    }

    copy(content: string): void {
        copy.text(content);

        this.messageService.add({
            severity: messageSeverityEnum.success,
            summary: this.localizationService.transform('AMT.accountSettings.mediaDatabaseApis.copy.summary'),
            detail: this.localizationService.transform('AMT.accountSettings.mediaDatabaseApis.copy.message'),
        });
    }

    refreshMediaDatabaseApiFields(): void {
        if (this.selectedAccount?.accessTokens?.length) {
            this.accessToken = this.selectedAccount?.accessTokens[0];
        } else {
            this.accessToken = null;
        }

        this.clientId = this.selectedAccount?.clientId;
    }

    hasUiAccessChanged(hasUiAccess: boolean): void {
        if (!hasUiAccess) {
            this.allAccountPermissions.forEach(x => {
                this.selectedAccount.permissions = this.selectedAccount.permissions.filter(p => p !== x.enum);
                this.togglePreferenceVisibilities(false, x.enum);
            });
        }
    }

    hasMediaDatabaseApiChanged(hasMediaDatabaseApi: boolean): void {
        if (hasMediaDatabaseApi) {
            this.addPermission(userPermissionsEnum.mediaDatabaseApi);
            if (!this.selectedMediaDatabaseApiReseller) {
                this.selectedMediaDatabaseApiReseller = this.mediaDatabaseApisDropdownOptions[0];
            }

            this.selectedAccount.mediaApiPartnerResellerId = this.selectedMediaDatabaseApiReseller.value;
        } else {
            this.removePermission(userPermissionsEnum.mediaDatabaseApi);
            this.selectedAccount.mediaApiPartnerResellerId = null;
        }
    }

    hasMediaMonitoringApiChanged(hasMediaMonitoringApi: boolean): void {
        if (hasMediaMonitoringApi) {
            this.addPermission(userPermissionsEnum.mediaMonitoringApi);
        } else {
            this.removePermission(userPermissionsEnum.mediaMonitoringApi);
        }
        this.accountManagementService.setMediaMonitoringApisPermission();
    }

    openRegionBrowser(includeOrExclude: IncludeOrExclude) {
        this.regionSelectDialogService.prepareData(includeOrExclude, this.targetingPreferences.geography[includeOrExclude]);
        const io: DataSelectDialogModel = {
            inputs: {
                id: 'targetingPreferences.regionSelectDialog',
                dialogService: this.regionSelectDialogService,
                dialogHeader: 'Monitoring.search.regions',
                dialogMessage: 'AMT.targeting.regionalRestrictions.browserMsg',
                leftColumnHeader: 'Monitoring.search.availableRegions',
                rightColumnHeader: 'Monitoring.search.selectedRegions'
            },
            outputs: {
                dialogClose: (selectedRegions: JournodexGeographyModel[]) => {
                    if (!selectedRegions) {
                        selectedRegions = [];
                    }

                    this.targetingPreferences.geography[includeOrExclude] = selectedRegions?.map(region => region.collectionId);

                    // HACK: pass by reference.. so the value never gets updated in the agpr-include-exclude-chips
                    const geo = this.targetingPreferences.geography;
                    this.targetingPreferences.geography = {
                        include: geo.include.slice(),
                        exclude: geo.exclude.slice()
                    } as BasicFilterApiModel<number>;
                }
            }
        };
        this.coreDialogService.open(DataSelectDialogComponent, io);
    }

    handleNoteChange(event: AccountNoteChange) {
        this.accountManagementService.performAccountNoteAction(event.type, event.note, this.selectedAccount.id);
    }
    onNetworksErrorChange(hasError: boolean) {
        this.hasNetworksError = hasError;
        this.saveErrorTooltip = hasError ? [
            this.localizationService.transform('Social.agentWidget.error.general.heading'),
            `• ${this.localizationService.transform('AMT.social.networks.errorMessage')}`
        ].join('\n') : '';
    }
}
